/**
 * Loop-breaker for the PLT-3933 force-relogin safety net (PLT-5124). Persists a
 * short-lived marker across the forced-relogin page reload so the safety net can
 * tell a genuine session expiry (force relogin once) from a tight back-to-back
 * bounce (suppress and stop looping). See `hooks.ts` for the loop mechanism.
 *
 * `sessionStorage` (not `localStorage`) so the marker survives the hard
 * navigation but doesn't leak across tabs/restarts; all access is try/caught as
 * it can throw in private-mode contexts.
 */

const LAST_FORCED_RELOGIN_KEY = 'upright:lastForcedReloginAt';

// A second disagreement within this window is treated as a loop, not a new
// expiry. The bounce completes in well under a second; 15s is a safe margin.
const LOOP_GUARD_WINDOW_MS = 15_000;

/** Record that a relogin was just forced. Called on every forced bounce to `/login`. */
export function recordForcedRelogin(): void {
  try {
    window.sessionStorage.setItem(LAST_FORCED_RELOGIN_KEY, String(Date.now()));
  } catch {
    // Without the marker the guard just falls back to pre-PLT-5124 behaviour.
  }
}

/** True if a relogin was forced within the last `LOOP_GUARD_WINDOW_MS`. */
export function hasRecentlyForcedRelogin(): boolean {
  try {
    const raw = window.sessionStorage.getItem(LAST_FORCED_RELOGIN_KEY);
    if (!raw) return false;
    const lastAt = Number(raw);
    if (!Number.isFinite(lastAt)) return false;
    return Date.now() - lastAt < LOOP_GUARD_WINDOW_MS;
  } catch {
    return false;
  }
}

/** Clear the marker once auth is healthy again, so a later expiry isn't mistaken for a loop. */
export function clearForcedReloginMarker(): void {
  try {
    window.sessionStorage.removeItem(LAST_FORCED_RELOGIN_KEY);
  } catch {
    // No-op — see recordForcedRelogin.
  }
}
